<template>
  <div class="track-timeline-container">
    <ul v-if="timelineList && timelineList.length > 0" class="timeline">
      <li
        v-for="(item, index) in visibleList"
        :key="index"
        :class="['tl-item', { latest: index === 0 }]"
      >
        <span class="tl-dot" />

        <!-- 最新一条首行带卡片高亮背景 -->
        <div v-if="index === 0" class="card">
          <div class="row">
            <span class="tl-time">{{ item.dateTime || '--' }}</span>
            <div class="tl-desc">
              <span>{{ item.status || '--' }}</span>
              <button
                v-if="hasPhoto(item)"
                class="view-photo-btn"
                type="button"
                @click.stop="onViewPhoto(item)"
              >
                <i class="el-icon-picture-outline" style="margin-right: 2px;" />
                {{ $t('trajectory.viewSignImage') }}
              </button>
              <button
                v-if="hasProblemPhoto(item)"
                class="view-photo-btn view-problem-photo-btn"
                type="button"
                @click.stop="onViewProblemPhoto(item)"
              >
                <i class="el-icon-warning-outline" style="margin-right: 2px;" />
                {{ $t('trajectory.viewProblemImage') }}
              </button>
            </div>
            <span class="tl-op">{{ item.operateUserName || item.siteName || '--' }}</span>
            <span class="tl-loc">{{ item.address || item.siteName || '--' }}</span>
          </div>
        </div>

        <!-- 历史轨迹普通行 -->
        <div v-else class="row">
          <span class="tl-time">{{ item.dateTime || '--' }}</span>
          <div class="tl-desc">
            <span>{{ item.status || '--' }}</span>
            <button
              v-if="hasPhoto(item)"
              class="view-photo-btn"
              type="button"
              @click.stop="onViewPhoto(item)"
            >
              <i class="el-icon-picture-outline" style="margin-right: 2px;" />
              {{ $t('trajectory.viewSignImage') }}
            </button>
            <button
              v-if="hasProblemPhoto(item)"
              class="view-photo-btn view-problem-photo-btn"
              type="button"
              @click.stop="onViewProblemPhoto(item)"
            >
              <i class="el-icon-warning-outline" style="margin-right: 2px;" />
              {{ $t('trajectory.viewProblemImage') }}
            </button>
          </div>
          <span class="tl-op">{{ item.operateUserName || item.siteName || '--' }}</span>
          <span class="tl-loc">{{ item.address || item.siteName || '--' }}</span>
        </div>
      </li>
    </ul>

    <!-- 展开 / 收起更早轨迹 -->
    <div v-if="timelineList.length > defaultVisibleCount" class="more">
      <button type="button" @click="toggleExpand">
        {{ isExpanded ? $t('trajectory.collapseEarlier') : $t('trajectory.expandEarlier') }}
      </button>
    </div>

    <!-- 暂无轨迹提示 -->
    <div v-if="!timelineList || timelineList.length === 0" class="empty-timeline">
      <i class="el-icon-document-delete" style="font-size: 28px; color: #c0c4cc; margin-bottom: 8px;" />
      <span style="color: #909399; font-size: 13px;">{{ $t('trajectory.noTrackInfo') }}</span>
    </div>
  </div>
</template>

<script>
/**
 * 物流轨迹时间轴展示组件
 *
 * 核心功能：
 * 1. 列表渲染：按时间倒序展示运单各个节点的流转状态、网点/操作人、地址等信息；
 * 2. 最新高亮：首行最新记录呈现淡绿色轻量呼吸高亮卡片（包含绿色呼吸点与首段渐变连接线）；
 * 3. 折叠展开：当轨迹总记录数超过阈值（默认 20 条）时自动折叠历史记录，提供展开/收起切换按钮；
 * 4. 签收凭证：签收节点（statusCode 为 signed 或 13）若包含签收照片/签名，展示【查看签收图片】按钮；
 * 5. 问题件凭证：异常节点（包含 problemType 或 problemTypeSub）若包含异常图片，展示【查看问题件图片】按钮。
 */
export default {
  name: 'TrackTimeline',
  props: {
    /**
     * 轨迹列表数据
     * @property {string} dateTime 轨迹发生时间（如 2026-09-01 10:20:00）
     * @property {string} status 轨迹节点状态描述
     * @property {string} operateUserName 操作人姓名
     * @property {string} siteName 操作网点名称
     * @property {string} address 轨迹发生地址
     * @property {string|number} statusCode 轨迹节点状态编码（如 'signed'、'13'）
     * @property {string|Array} deliverdPhoto 签收现场照片（JSON 字符串、纯 URL 或 URL 数组）
     * @property {string|Array} deliverdNamePhoto 签收签名照片（JSON 字符串、纯 URL 或 URL 数组）
     * @property {string|number} problemType 一级问题件类型
     * @property {string|number} problemTypeSub 二级问题件类型
     * @property {string|Array} exceptionImageUrls 异常件图片列表（JSON 字符串、逗号拼接或数组）
     * @property {string|Array} problemItemImg 历史问题件图片字段
     */
    timelineList: {
      type: Array,
      default: () => []
    },
    /**
     * 默认展示的轨迹记录条数，超过此阈值折叠更早轨迹
     */
    defaultVisibleCount: {
      type: Number,
      default: 20
    }
  },
  data() {
    return {
      /**
       * 是否已展开更早的历史轨迹
       */
      isExpanded: false
    };
  },
  computed: {
    /**
     * 当前可视的轨迹切片列表
     * 当未展开且总数超过 defaultVisibleCount 时，仅截取前 N 条
     */
    visibleList() {
      if (!this.timelineList) return [];
      if (this.isExpanded || this.timelineList.length <= this.defaultVisibleCount) {
        return this.timelineList;
      }
      return this.timelineList.slice(0, this.defaultVisibleCount);
    }
  },
  methods: {
    /**
     * 切换展开 / 收起历史轨迹
     */
    toggleExpand() {
      this.isExpanded = !this.isExpanded;
    },
    /**
     * 判断当前轨迹节点是否属于【已签收】节点
     * 规则：满足以下任一条件即认定为签收节点：
     * 1. statusCode 编码为 'signed' 或 '13'
     * 2. 状态文本包含 '已签收' 或葡语 'entregue'
     * 3. 包含有效 signType 签收类型标识
     * @param {Object} item 轨迹单项对象
     * @returns {boolean} 是否为签收节点
     */
    isSignedTrack(item) {
      if (!item) return false;
      const code = String(item.statusCode || '').toLowerCase();
      if (code === 'signed' || code === '13') return true;
      const statusText = String(item.status || '').toLowerCase();
      return statusText.includes('已签收') || statusText.includes('entregue') || Boolean(item.signType);
    },
    /**
     * 校验图片字段是否包含真实的有效图片数据（防脏数据容错处理）
     * 支持字符串（JSON 数组字符串、纯 URL 文本）与原生数组，排除空数组 '[]'、空对象 '{}' 或 'null'
     * @param {string|Array} photo 待校验的图片字段值
     * @returns {boolean} 是否包含有效图片 URL
     */
    hasValidPhotoData(photo) {
      if (!photo) return false;
      if (typeof photo === 'string') {
        const trimmed = photo.trim();
        if (!trimmed || trimmed === '[]' || trimmed === '{}' || trimmed === 'null') {
          return false;
        }
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) {
            return parsed.some(p => Boolean(p && String(p).trim()));
          }
        } catch (e) {
          return trimmed.length > 0;
        }
      } else if (Array.isArray(photo)) {
        return photo.some(p => Boolean(p && String(p).trim()));
      }
      return Boolean(photo);
    },
    /**
     * 判断当前轨迹项是否展示【查看签收图片】按钮
     * 规则：必须是签收节点（signed / 13），且电子签名或现场签收照片中至少有一项包含有效数据
     * @param {Object} item 轨迹单项对象
     * @returns {boolean} 是否展示签收图片按钮
     */
    hasPhoto(item) {
      if (!item) return false;
      // 必须是签收轨迹，且存在有效图片才展示查看签收图片按钮
      if (!this.isSignedTrack(item)) return false;
      return this.hasValidPhotoData(item.deliverdPhoto) || this.hasValidPhotoData(item.deliverdNamePhoto);
    },
    /**
     * 点击【查看签收图片】按钮，向父组件抛出事件
     * @param {Object} item 当前轨迹单项对象
     */
    onViewPhoto(item) {
      this.$emit('view-photo', item);
    },
    /**
     * 判断当前轨迹节点是否属于【问题件/异常件】节点
     * @param {Object} item 轨迹单项对象
     * @returns {boolean} 是否包含 problemType 或 problemTypeSub
     */
    isProblemTrack(item) {
      if (!item) return false;
      return !!(item.problemType || item.problemTypeSub);
    },
    /**
     * 判断当前轨迹项是否展示【查看问题件图片】按钮
     * 规则：必须属于问题件节点，且异常图片字段中包含有效数据
     * @param {Object} item 轨迹单项对象
     * @returns {boolean} 是否展示问题件图片按钮
     */
    hasProblemPhoto(item) {
      if (!item) return false;
      if (!this.isProblemTrack(item)) return false;
      return this.hasValidPhotoData(item.exceptionImageUrls) || this.hasValidPhotoData(item.problemItemImg);
    },
    /**
     * 点击【查看问题件图片】按钮，向父组件抛出事件
     * @param {Object} item 当前轨迹单项对象
     */
    onViewProblemPhoto(item) {
      this.$emit('view-problem-photo', item);
    }
  }
};
</script>

<style scoped>
.track-timeline-container {
  padding: 6px 0 16px;
  box-sizing: border-box;
}

.timeline {
  list-style: none;
  margin: 0;
  padding: 0;
}

.tl-item {
  position: relative;
  padding: 0 14px 18px 24px;
  box-sizing: border-box;
}

.tl-item::before {
  content: "";
  position: absolute;
  left: 4px;
  top: 14px;
  bottom: -2px;
  width: 2px;
  background: #E5E6EB;
}

.tl-item:last-child::before {
  display: none;
}

.tl-dot {
  position: absolute;
  left: 0;
  top: 5px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #00B578;
}

.tl-item .row {
  display: grid;
  grid-template-columns: 150px minmax(0, 1fr) 220px 150px;
  gap: 12px;
  align-items: start;
}

.tl-time {
  font-size: 13px;
  color: #86909C;
  font-variant-numeric: tabular-nums;
  line-height: 1.5;
}

.tl-desc {
  font-size: 13px;
  color: #4E5969;
  line-height: 1.5;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.tl-op {
  font-size: 13px;
  color: #4E5969;
  word-break: break-all;
  line-height: 1.5;
}

.tl-loc {
  font-size: 13px;
  color: #4E5969;
  text-align: right;
  line-height: 1.5;
  word-break: break-all;
}

/* 最新一条：轻量呼吸高亮卡片 */
.tl-item.latest .tl-dot {
  width: 12px;
  height: 12px;
  left: -1px;
  top: 4px;
  box-shadow: 0 0 0 3px rgba(0, 181, 120, 0.18);
}

.tl-item.latest::before {
  background: linear-gradient(to bottom, #00B578, #E5E6EB);
}

.tl-item.latest .card {
  background: #F4FBF6;
  border-radius: 6px;
  padding: 10px 14px;
  /* 负外边距抵消内边距，保证与其他行的四列完全对齐 */
  margin: 0 -14px;
}

.tl-item.latest .tl-time {
  color: #4E5969;
  font-weight: 600;
}

.tl-item.latest .tl-desc {
  color: #00B42A;
  font-weight: 600;
}

.tl-item.latest .tl-op {
  color: #4E5969;
}

.tl-item.latest .tl-loc {
  color: #00B42A;
  font-weight: 500;
}

.more {
  margin: 4px 0 0 24px;
}

.more button {
  background: none;
  border: none;
  color: #86909C;
  font-size: 13px;
  cursor: pointer;
  padding: 4px 0;
  outline: none;
  transition: color 0.2s ease;
}

.more button:hover {
  color: #00B578;
}

.view-photo-btn {
  background: none;
  border: 1px solid #00B578;
  color: #00B578;
  border-radius: 4px;
  padding: 1px 6px;
  font-size: 11px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  margin-left: 6px;
  line-height: 16px;
  transition: all 0.2s ease;
}

.view-photo-btn:hover {
  background: #E8FFF3;
}

.view-problem-photo-btn {
  border-color: #FA8C16;
  color: #FA8C16;
}

.view-problem-photo-btn:hover {
  background: #FFF7E6;
}

.empty-timeline {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
}
</style>
